import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Translate } from 'react-localize-redux';
import useDictatedInput from '../hooks/useDictatedInput';
import useAttachments from '../hooks/useAttachments';
import { collectHtmlArtifacts } from '../htmlArtifacts';
import { HtmlArtifactShelfProvider } from './artifacts/htmlArtifactShelfContext';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatEmptyState from './ChatEmptyState';
import ArtifactShelf from './ArtifactShelf';
import AgentActivity from './AgentActivity';
import ConfirmationCard from './ConfirmationCard';
import SessionDebugPanel from './SessionDebugPanel';
import Toast from './Toast';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import SlackIcon from '../../resources/svg/icons/slack-icon.svg';
import {
    AGENT_STATE,
    ATTACHMENT_BLOCK_TYPE,
    CHAT_EVENTS,
    DEFAULT_ATTACHMENT_PROMPT_KEY,
    FEEDBACK_TOAST_VARIANT,
    MESSAGE_ROLES,
    STEP_STATUS,
    SUBMIT_STATUS,
    isReadOnlySessionType,
    trackChatEvent,
} from '../constants';
import { getAdminModeEnabled } from '../../selectors/user';
import { getTranslate } from '../../selectors/locale';
import css from './ChatConversation.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT';

const SCROLL_BOTTOM_THRESHOLD_PX = 50;

const ChatConversation = ({
    chat,
    showEmptyState = false,
    showSessionDebug = false,
    onSessionDebugClose = () => {},
    onShelfOpenChange,
    maximizeShelfOnOpen = false,
}) => {
    const {
        sessionType,
        messages,
        input,
        setInput,
        handleSubmit,
        isLoading,
        isCancelling,
        activeRunId,
        pendingConfirmation,
        resolveConfirmation,
        discardConfirmation,
        error,
        stopError,
        reload,
        stop,
        currentSessionId,
        currentSessionType = sessionType,
        subscribeToClearCurrentSession,
    } = chat;

    const messagesListRef = useRef(null);
    const pendingScrollFrameRef = useRef(0);

    const scrollMessagesToBottom = useCallback(() => {
        const list = messagesListRef.current;
        if (!list) return;
        list.scrollTop = list.scrollHeight;
    }, []);

    const [openArtifactId, setOpenArtifactId] = useState(null);
    const [isShelfMaximized, setIsShelfMaximized] = useState(false);
    const lastSeenArtifactIdRef = useRef(undefined);

    const [artifactsSessionId, setArtifactsSessionId] = useState(currentSessionId);
    if (artifactsSessionId !== currentSessionId) {
        setArtifactsSessionId(currentSessionId);
        setOpenArtifactId(null);
        setIsShelfMaximized(false);
        lastSeenArtifactIdRef.current = undefined;
    }

    const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
    const isPinnedToBottomRef = useRef(true);
    isPinnedToBottomRef.current = isPinnedToBottom;

    const [feedbackToast, setFeedbackToast] = useState(null);

    useEffect(() => {
        if (!feedbackToast) return undefined;
        const timeoutId = setTimeout(() => setFeedbackToast(null), 5000);
        return () => clearTimeout(timeoutId);
    }, [feedbackToast]);

    const handleFeedbackSubmitted = useCallback(() => setFeedbackToast(FEEDBACK_TOAST_VARIANT.SUCCESS), []);
    const handleFeedbackFailed = useCallback(() => setFeedbackToast(FEEDBACK_TOAST_VARIANT.FAILURE), []);

    const isAdminMode = useSelector(getAdminModeEnabled);
    const translate = useSelector(getTranslate);

    const prevSessionIdRef = useRef(currentSessionId);

    const isAwaitingConfirmation = Boolean(pendingConfirmation);
    // A parked run keeps its entry in activeRuns, so isLoading stays true while it waits on a
    // human. The composer and the activity indicator have to read it as stopped, not running.
    const isRunning = isLoading && !isAwaitingConfirmation;
    const isInputDisabled = isRunning || isCancelling;
    const isReadOnly = isReadOnlySessionType(currentSessionType);

    const hasMessages = messages.length > 0;
    const prevMessagesCountRef = useRef(messages.length);

    const handleScroll = useCallback(() => {
        const list = messagesListRef.current;
        if (!list) return;
        const distanceFromBottom = list.scrollHeight - list.clientHeight - list.scrollTop;
        const atBottom = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
        setIsPinnedToBottom(prev => (prev === atBottom ? prev : atBottom));
    }, []);

    useEffect(() => () => cancelAnimationFrame(pendingScrollFrameRef.current), []);

    const [completedRunCount, setCompletedRunCount] = useState(0);
    const prevIsLoadingRef = useRef(isLoading);
    useEffect(() => {
        if (prevIsLoadingRef.current && !isLoading) setCompletedRunCount(c => c + 1);
        prevIsLoadingRef.current = isLoading;
    }, [isLoading]);

    const {
        isListening: isMicListening,
        isSupported: isMicSupported,
        toggleDictation: toggleMic,
        stopDictation: stopMic,
        onManualChange,
        finishDictation,
        markVoiceUnused,
    } = useDictatedInput({ value: input, onChange: setInput });

    const onMicClick = useCallback(() => {
        if (isMicListening) {
            trackChatEvent(CHAT_EVENTS.MICROPHONE_STOP, {
                sessionId: currentSessionId,
                hasTranscript: !!input.trim(),
            });
        } else {
            trackChatEvent(CHAT_EVENTS.MICROPHONE_START, { sessionId: currentSessionId });
        }
        toggleMic();
    }, [toggleMic, input, isMicListening, currentSessionId]);

    const handleStop = useCallback(() => {
        trackChatEvent(CHAT_EVENTS.STOP_GENERATION, { sessionId: currentSessionId, runId: activeRunId });
        stop();
    }, [stop, currentSessionId, activeRunId]);

    useEffect(() => {
        if (isLoading && isMicListening) stopMic();
    }, [isLoading, isMicListening, stopMic]);

    useEffect(() => {
        const sessionChanged = prevSessionIdRef.current !== currentSessionId;
        prevSessionIdRef.current = currentSessionId;

        if (sessionChanged) {
            prevMessagesCountRef.current = messages.length;
            scrollMessagesToBottom();
            setIsPinnedToBottom(true);
            if (isMicListening) stopMic();
            markVoiceUnused();
            return;
        }

        const grew = messages.length > prevMessagesCountRef.current;
        prevMessagesCountRef.current = messages.length;

        if (grew && messages[messages.length - 1]?.role === MESSAGE_ROLES.USER) {
            setIsPinnedToBottom(true);
            scrollMessagesToBottom();
            return;
        }

        if (!isPinnedToBottomRef.current) return;
        if (pendingScrollFrameRef.current) return;
        pendingScrollFrameRef.current = requestAnimationFrame(() => {
            pendingScrollFrameRef.current = 0;
            scrollMessagesToBottom();
        });
    }, [messages, currentSessionId, isMicListening, stopMic, markVoiceUnused, scrollMessagesToBottom]);

    const attachmentsApi = useAttachments();
    const { readyAttachments, readyUploadIds, clearAttachments, discardAttachments } = attachmentsApi;

    // Attachments are not keyed by session, so drop them when the user opens another
    // conversation. A new chat adopting the id /runs handed back is not a switch: its
    // previous id was null, and its files were staged for the message just sent.
    const attachmentsSessionIdRef = useRef(currentSessionId);
    useEffect(() => {
        const previousSessionId = attachmentsSessionIdRef.current;
        attachmentsSessionIdRef.current = currentSessionId;
        if (previousSessionId && previousSessionId !== currentSessionId) discardAttachments();
    }, [currentSessionId, discardAttachments]);

    // A session can also be cleared without its id moving, which the effect above
    // cannot see: the topbar hands its prompt to the auto-run effect, which clears and
    // then submits through handleSubmit directly, never through submitWithMicOff - so
    // anything staged here would be neither sent nor released.
    useEffect(() => subscribeToClearCurrentSession?.(discardAttachments), [
        subscribeToClearCurrentSession,
        discardAttachments,
    ]);

    const submitWithMicOff = useCallback(
        (e, options) => {
            const usedVoice = finishDictation();
            const hasAttachments = readyUploadIds.length > 0;
            let content = options?.content ?? input ?? '';
            if (!content.trim() && hasAttachments) content = translate(DEFAULT_ATTACHMENT_PROMPT_KEY);
            const messageLength = content.trim().length;
            if (messageLength) {
                trackChatEvent(CHAT_EVENTS.SEND_MESSAGE, {
                    sessionId: currentSessionId,
                    isNewSession: !currentSessionId,
                    messageLength,
                    usedVoice,
                    attachmentCount: readyUploadIds.length,
                    is_top_nav: false,
                });
            }
            const submitOptions = hasAttachments
                ? {
                      ...options,
                      content,
                      attachments: readyUploadIds,
                      attachmentBlocks: readyAttachments.map(att => ({
                          type: ATTACHMENT_BLOCK_TYPE,
                          name: att.name,
                          kind: att.kind,
                          size_bytes: att.sizeBytes,
                          previewUrl: att.previewUrl,
                      })),
                  }
                : options;
            const sentLocalIds = readyAttachments.map(att => att.localId);
            const result = handleSubmit(e, submitOptions);
            // Only a created run owns the ids; anything else leaves the chips in
            // place so the user can retry with them.
            Promise.resolve(result)
                .then(status => {
                    if (status === SUBMIT_STATUS.CREATED) clearAttachments(sentLocalIds);
                })
                .catch(() => {});
            return result;
        },
        [
            handleSubmit,
            finishDictation,
            input,
            currentSessionId,
            readyAttachments,
            readyUploadIds,
            clearAttachments,
            translate,
        ]
    );

    // Retry goes through submitWithMicOff so it resends the chips still staged in
    // the composer; the bare handleSubmit would drop them.
    const handleRegenerate = useCallback(() => {
        trackChatEvent(CHAT_EVENTS.REGENERATE_RESPONSE, { sessionId: currentSessionId });
        reload(submitWithMicOff);
    }, [reload, submitWithMicOff, currentSessionId]);

    const onInputChange = useCallback(e => onManualChange(e.target.value), [onManualChange]);

    // A picked prompt fills the composer, so a live mic would keep appending to it.
    const onSuggestionPicked = useCallback(
        query => {
            if (isMicListening) stopMic();
            markVoiceUnused();
            onManualChange(query);
        },
        [isMicListening, stopMic, markVoiceUnused, onManualChange]
    );

    const lastAssistantMessage = useMemo(() => messages.findLast(msg => msg.role === MESSAGE_ROLES.ASSISTANT) || null, [
        messages,
    ]);

    const agentActivity = useMemo(() => {
        if (!lastAssistantMessage) return null;
        const { steps = [], startedAt, finishedAt } = lastAssistantMessage;
        const finishedSteps = steps.filter(s => s.status === STEP_STATUS.COMPLETED || s.status === STEP_STATUS.FAILED);
        const runningStep = steps.find(s => s.status === STEP_STATUS.RUNNING);

        // Parked on a confirmation: nothing is in flight, but the turn is not over either, so
        // it reports neither the spinner nor "analysis complete".
        if (isAwaitingConfirmation) {
            return {
                state: AGENT_STATE.WAITING,
                currentTool: null,
                history: finishedSteps,
                totalDurationMs: 0,
            };
        }

        if (!isRunning) {
            if (finishedSteps.length === 0) return null;
            const totalMs = startedAt && finishedAt ? finishedAt - startedAt : 0;
            return {
                state: AGENT_STATE.DONE,
                currentTool: null,
                history: finishedSteps,
                totalDurationMs: totalMs,
            };
        }

        if (finishedAt) {
            return {
                state: AGENT_STATE.THINKING,
                currentTool: null,
                history: [],
                totalDurationMs: 0,
            };
        }

        if (runningStep) {
            return {
                state: AGENT_STATE.RUNNING,
                currentTool: { name: runningStep.title || 'Working' },
                history: finishedSteps,
                totalDurationMs: 0,
            };
        }

        return {
            state: AGENT_STATE.THINKING,
            currentTool: null,
            history: finishedSteps,
            totalDurationMs: 0,
        };
    }, [lastAssistantMessage, isRunning, isAwaitingConfirmation]);

    const htmlArtifacts = useMemo(() => collectHtmlArtifacts(messages), [messages]);
    const newestArtifactId = htmlArtifacts[0]?.id ?? null;
    const newestArtifactRunId = htmlArtifacts[0]?.runId ?? null;

    useEffect(() => {
        const lastSeen = lastSeenArtifactIdRef.current;
        lastSeenArtifactIdRef.current = newestArtifactId;

        if (!newestArtifactId || lastSeen === newestArtifactId) return;
        const isFromLiveRun = Boolean(activeRunId) && newestArtifactRunId === activeRunId;
        if (lastSeen === undefined && !isFromLiveRun) return;

        setOpenArtifactId(newestArtifactId);
        setIsShelfMaximized(maximizeShelfOnOpen);
    }, [newestArtifactId, newestArtifactRunId, activeRunId, maximizeShelfOnOpen]);

    const showArtifact = useCallback(
        artifactId => {
            setOpenArtifactId(artifactId);
            setIsShelfMaximized(maximizeShelfOnOpen);
        },
        [maximizeShelfOnOpen]
    );

    const closeShelf = useCallback(() => {
        setOpenArtifactId(null);
        setIsShelfMaximized(false);
    }, []);

    const toggleShelfMaximized = useCallback(() => setIsShelfMaximized(maximized => !maximized), []);

    const htmlArtifactShelf = useMemo(() => ({ artifacts: htmlArtifacts, openArtifactId, showArtifact }), [
        htmlArtifacts,
        openArtifactId,
        showArtifact,
    ]);

    const isShelfOpen = htmlArtifacts.some(artifact => artifact.id === openArtifactId);
    const isChatHidden = isShelfOpen && isShelfMaximized;

    useEffect(() => {
        onShelfOpenChange?.(isShelfOpen);
    }, [isShelfOpen, onShelfOpenChange]);

    const [hasUnreadWhileChatHidden, setHasUnreadWhileChatHidden] = useState(false);
    const hiddenChatMessageCountRef = useRef(messages.length);
    useEffect(() => {
        const grew = messages.length > hiddenChatMessageCountRef.current;
        hiddenChatMessageCountRef.current = messages.length;
        if (!isChatHidden) setHasUnreadWhileChatHidden(false);
        else if (grew) setHasUnreadWhileChatHidden(true);
    }, [messages.length, isChatHidden]);

    const scrollToBottom = useCallback(() => {
        scrollMessagesToBottom();
        setIsPinnedToBottom(true);
    }, [scrollMessagesToBottom]);

    return (
        <HtmlArtifactShelfProvider value={htmlArtifactShelf}>
            <div className={css.conversation}>
                {feedbackToast && (
                    <Toast
                        icon={feedbackToast === FEEDBACK_TOAST_VARIANT.SUCCESS ? 'check' : 'alert-triangle'}
                        onClose={() => setFeedbackToast(null)}
                    >
                        <Translate
                            id={`${TRANSLATION_PREFIX}.FEEDBACK.${
                                feedbackToast === FEEDBACK_TOAST_VARIANT.SUCCESS ? 'SUBMITTED_TOAST' : 'FAILED_TOAST'
                            }`}
                        />
                    </Toast>
                )}
                <div className={classNames(css.content, { [css.contentCollapsed]: isChatHidden })}>
                    <div className={css.messagesContainer}>
                        {showEmptyState && !hasMessages && !isReadOnly && (
                            <ChatEmptyState
                                onSuggestionClick={onSuggestionPicked}
                                input={input}
                                handleInputChange={onInputChange}
                                handleSubmit={submitWithMicOff}
                                isLoading={isInputDisabled}
                                stop={handleStop}
                                onMicClick={onMicClick}
                                isMicListening={isMicListening}
                                micSupported={isMicSupported}
                                attachments={attachmentsApi}
                            />
                        )}
                        {hasMessages && (
                            <div ref={messagesListRef} className={css.messagesList} onScroll={handleScroll}>
                                {messages.map(message => (
                                    <ChatMessage
                                        key={message.id}
                                        message={message}
                                        sessionId={currentSessionId}
                                        sessionType={currentSessionType}
                                        isReadOnly={isReadOnly}
                                        isAdminMode={isAdminMode}
                                        onFeedbackSubmitted={handleFeedbackSubmitted}
                                        onFeedbackFailed={handleFeedbackFailed}
                                    />
                                ))}
                            </div>
                        )}
                        {pendingConfirmation && (
                            <div className={css.confirmation}>
                                <ConfirmationCard
                                    confirmation={pendingConfirmation}
                                    onResolve={(confirmed, note) => resolveConfirmation(confirmed, { note })}
                                    onDiscard={() => discardConfirmation()}
                                />
                            </div>
                        )}
                        {hasMessages && !isPinnedToBottom && !error && !stopError && (
                            <button
                                type="button"
                                className={css.jumpToBottom}
                                onClick={scrollToBottom}
                                aria-label="Scroll to latest message"
                            >
                                <Icon name="chevron-down" size={IconSizes.SMALL} interactive={false} />
                            </button>
                        )}
                        {(error || stopError) && (
                            <div className={css.chatError}>
                                <Icon
                                    name="alert-triangle"
                                    variant={IconVariants.ERROR}
                                    size={IconSizes.SMALL}
                                    className={css.chatErrorIcon}
                                    interactive={false}
                                />
                                <span className={css.chatErrorMessage}>{error || stopError}</span>
                                {error && !isReadOnly && (
                                    <button onClick={handleRegenerate} className={css.chatErrorRetry}>
                                        <Translate id={`${TRANSLATION_PREFIX}.RETRY`} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    {isAdminMode && showSessionDebug && (
                        <SessionDebugPanel
                            sessionId={currentSessionId}
                            refreshKey={completedRunCount}
                            onClose={onSessionDebugClose}
                        />
                    )}
                    {(hasMessages || isReadOnly) && (
                        <div className={css.bottomDock}>
                            {agentActivity && (
                                <AgentActivity
                                    state={agentActivity.state}
                                    currentTool={agentActivity.currentTool}
                                    history={agentActivity.history}
                                    totalDurationMs={agentActivity.totalDurationMs}
                                />
                            )}
                            {isAwaitingConfirmation && input.trim() && (
                                <div className={css.confirmationWarning}>
                                    <Icon
                                        name="alert-triangle"
                                        variant={IconVariants.WARNING}
                                        size={IconSizes.SMALL}
                                        interactive={false}
                                    />
                                    <Translate id={`${TRANSLATION_PREFIX}.CONFIRMATION.SEND_DISCARDS_WARNING`} />
                                </div>
                            )}
                            {isReadOnly ? (
                                <div className={css.readOnlyNotice}>
                                    <SlackIcon className={css.slackIcon} />
                                    <Translate id={`${TRANSLATION_PREFIX}.SLACK_READ_ONLY_NOTICE`} />
                                </div>
                            ) : (
                                <ChatInput
                                    input={input}
                                    handleInputChange={onInputChange}
                                    handleSubmit={submitWithMicOff}
                                    isLoading={isInputDisabled}
                                    stop={handleStop}
                                    onMicClick={onMicClick}
                                    isMicListening={isMicListening}
                                    micSupported={isMicSupported}
                                    attachments={attachmentsApi}
                                />
                            )}
                        </div>
                    )}
                </div>
                {isChatHidden && (
                    <button
                        type="button"
                        className={css.chatEdgeTab}
                        onClick={toggleShelfMaximized}
                        aria-label={translate(`${TRANSLATION_PREFIX}.ARTIFACT_SHELF.SHOW_CHAT`)}
                    >
                        <Icon name="message" size={IconSizes.SMALL} interactive={false} />
                        {hasUnreadWhileChatHidden && <span className={css.chatEdgeTabDot} />}
                    </button>
                )}
                {isShelfOpen && (
                    <ArtifactShelf
                        artifacts={htmlArtifacts}
                        openArtifactId={openArtifactId}
                        isMaximized={isShelfMaximized}
                        onShowArtifact={showArtifact}
                        onToggleMaximized={toggleShelfMaximized}
                        onClose={closeShelf}
                    />
                )}
            </div>
        </HtmlArtifactShelfProvider>
    );
};

ChatConversation.propTypes = {
    chat: PropTypes.shape({
        sessionType: PropTypes.string,
        messages: PropTypes.arrayOf(PropTypes.object).isRequired,
        input: PropTypes.string.isRequired,
        setInput: PropTypes.func.isRequired,
        handleInputChange: PropTypes.func.isRequired,
        handleSubmit: PropTypes.func.isRequired,
        isLoading: PropTypes.bool.isRequired,
        isCancelling: PropTypes.bool.isRequired,
        activeRunId: PropTypes.string,
        pendingConfirmation: PropTypes.shape({
            toolName: PropTypes.string,
            toolLabel: PropTypes.string,
            input: PropTypes.object,
            expiresAt: PropTypes.number,
            status: PropTypes.string,
            error: PropTypes.string,
        }),
        resolveConfirmation: PropTypes.func,
        discardConfirmation: PropTypes.func,
        error: PropTypes.string,
        stopError: PropTypes.string,
        reload: PropTypes.func.isRequired,
        stop: PropTypes.func.isRequired,
        currentSessionId: PropTypes.string,
        currentSessionType: PropTypes.string,
        subscribeToClearCurrentSession: PropTypes.func,
    }).isRequired,
    showEmptyState: PropTypes.bool,
    showSessionDebug: PropTypes.bool,
    onSessionDebugClose: PropTypes.func,
    onShelfOpenChange: PropTypes.func,
    maximizeShelfOnOpen: PropTypes.bool,
};

export default ChatConversation;
